import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, KeyRound, LoaderCircle, LogIn, LogOut, Mail, Ticket, UserPlus, UserRound } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import DigitalTicket from "@/components/DigitalTicket";
import { api } from "@/lib/api";
import { clearCustomerToken, customerHeaders, getCustomerToken, saveCustomerToken } from "@/lib/customerAuth";

const emptyRegistration = { name: "", email: "", phone: "", password: "", confirm: "" };

export default function MyAccount() {
  const [params, setParams] = useSearchParams();
  const verifyToken = params.get("verify") || "";
  const resetToken = params.get("reset") || "";
  const verificationStarted = useRef(false);
  const [mode, setMode] = useState(resetToken ? "reset" : verifyToken ? "verify" : "login");
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [registration, setRegistration] = useState(emptyRegistration);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [resetPasswords, setResetPasswords] = useState({ password: "", confirm: "" });
  const [pendingEmail, setPendingEmail] = useState("");
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(Boolean(getCustomerToken()) || Boolean(verifyToken));
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const config = { headers: customerHeaders() };
      const [userResponse, ticketsResponse] = await Promise.all([
        api.get("/auth/me", config),
        api.get("/account/tickets", config),
      ]);
      setUser(userResponse.data);
      setTickets(ticketsResponse.data);
    } catch (requestError) {
      clearCustomerToken();
      setUser(null);
      setError(requestError.response?.data?.detail || "Tu sesión ya no es válida.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (verifyToken || resetToken) return;
    if (getCustomerToken()) loadAccount();
  }, [loadAccount, resetToken, verifyToken]);

  useEffect(() => {
    if (!verifyToken || verificationStarted.current) return;
    verificationStarted.current = true;
    setLoading(true);
    setError("");
    api.get(`/auth/verify-email?token=${encodeURIComponent(verifyToken)}`)
      .then(async (response) => {
        saveCustomerToken(response.data.token);
        setParams({});
        setMessage("Tu correo fue verificado y la cuenta ya está activa.");
        await loadAccount();
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.detail || "No se pudo verificar el correo.");
        setMode("check-email");
        setLoading(false);
      });
  }, [loadAccount, setParams, verifyToken]);

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
    if (params.toString()) setParams({});
  };

  const login = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/auth/login", credentials);
      saveCustomerToken(response.data.token);
      await loadAccount();
    } catch (requestError) {
      const detail = requestError.response?.data?.detail || "No se pudo iniciar sesión.";
      setError(detail);
      if (requestError.response?.status === 403) {
        setPendingEmail(credentials.email);
        setMode("check-email");
      }
      setLoading(false);
    }
  };

  const register = async (event) => {
    event.preventDefault();
    setError("");
    if (registration.password !== registration.confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      const response = await api.post("/auth/register", {
        name: registration.name,
        email: registration.email,
        phone: registration.phone,
        password: registration.password,
      });
      setPendingEmail(registration.email);
      setMessage(response.data.message);
      setMode("check-email");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "No se pudo crear la cuenta.");
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    const email = pendingEmail || credentials.email || registration.email;
    if (!email) {
      setError("Captura el correo con el que te registraste.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/auth/resend-verification", { email });
      setMessage(response.data.message);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "No se pudo reenviar el correo.");
    } finally {
      setLoading(false);
    }
  };

  const requestRecovery = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await api.post("/auth/forgot-password", { email: recoveryEmail });
      setMessage(response.data.message);
      setMode("recovery-sent");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "No se pudo solicitar la recuperación.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (event) => {
    event.preventDefault();
    setError("");
    if (resetPasswords.password !== resetPasswords.confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    try {
      const response = await api.post("/auth/reset-password", {
        token: resetToken,
        password: resetPasswords.password,
      });
      saveCustomerToken(response.data.token);
      setParams({});
      setMessage("Tu contraseña se actualizó correctamente.");
      await loadAccount();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "No se pudo cambiar la contraseña.");
      setLoading(false);
    }
  };

  const logout = () => {
    clearCustomerToken();
    setUser(null);
    setTickets([]);
    setMode("login");
  };

  if (user) {
    return (
      <div className="section-pad">
        <div className="container-cg max-w-5xl">
          {message ? <div className="mb-6 border border-green-200 bg-green-50 p-3 text-sm text-green-800">{message}</div> : null}
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div>
              <div className="overline text-[var(--brand-primary)] flex items-center gap-2"><UserRound size={15} /> Cuenta de asistente</div>
              <h1 className="font-serif-cg text-4xl md:text-5xl mt-3">Hola, {user.name}.</h1>
              <p className="text-sm text-[var(--brand-muted)] mt-2">{user.email} · {tickets.length} {tickets.length === 1 ? "boleto" : "boletos"}</p>
            </div>
            <button type="button" onClick={logout} className="btn-secondary-cg"><LogOut size={16} /> Cerrar sesión</button>
          </div>
          {error ? <div className="mt-6 border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
          {tickets.length ? (
            <div className="grid lg:grid-cols-2 gap-6 mt-10">
              {tickets.map((ticket) => <DigitalTicket key={ticket.id} ticket={ticket} />)}
            </div>
          ) : (
            <div className="mt-10 border border-[var(--brand-border)] p-10 text-center">
              <Ticket size={30} className="mx-auto text-[var(--brand-primary)]" />
              <h2 className="font-serif-cg text-2xl mt-4">Todavía no hay boletos</h2>
              <p className="text-sm text-[var(--brand-muted)] mt-2">Las compras realizadas con {user.email} aparecerán aquí.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="section-pad">
      <div className="container-cg max-w-lg">
        <div className="overline text-[var(--brand-primary)]">Tus accesos</div>
        <h1 className="font-serif-cg text-4xl mt-3">Mi cuenta</h1>
        <p className="text-sm text-[var(--brand-muted)] mt-3 leading-relaxed">Consulta y descarga tus boletos desde cualquier dispositivo.</p>

        {mode === "login" || mode === "register" ? (
          <div className="grid grid-cols-2 border border-[var(--brand-border)] mt-7 bg-white">
            <button type="button" onClick={() => changeMode("login")} className={`px-4 py-3 text-sm font-semibold ${mode === "login" ? "bg-[var(--brand-primary)] text-white" : "text-[var(--brand-muted)]"}`}>Iniciar sesión</button>
            <button type="button" onClick={() => changeMode("register")} className={`px-4 py-3 text-sm font-semibold ${mode === "register" ? "bg-[var(--brand-primary)] text-white" : "text-[var(--brand-muted)]"}`}>Crear cuenta</button>
          </div>
        ) : null}

        <div className="card-cg bg-white p-6 mt-4">
          {mode === "verify" || (loading && verifyToken) ? (
            <div className="py-8 text-center"><LoaderCircle className="animate-spin mx-auto text-[var(--brand-primary)]" size={32} /><h2 className="font-serif-cg text-2xl mt-4">Verificando tu correo</h2></div>
          ) : mode === "register" ? (
            <form onSubmit={register} className="space-y-4">
              <div><UserPlus size={24} className="text-[var(--brand-primary)]" /><h2 className="font-serif-cg text-2xl mt-3">Crea tu cuenta</h2><p className="text-xs text-[var(--brand-muted)] mt-2">Te enviaremos un enlace para confirmar el correo.</p></div>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Nombre completo</span><input required autoComplete="name" value={registration.name} onChange={(event) => setRegistration({ ...registration, name: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Correo</span><input required type="email" autoComplete="email" value={registration.email} onChange={(event) => setRegistration({ ...registration, email: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Teléfono celular</span><input required type="tel" minLength={7} autoComplete="tel" value={registration.phone} onChange={(event) => setRegistration({ ...registration, phone: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Contraseña</span><input required type="password" minLength={8} autoComplete="new-password" value={registration.password} onChange={(event) => setRegistration({ ...registration, password: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Confirmar contraseña</span><input required type="password" minLength={8} autoComplete="new-password" value={registration.confirm} onChange={(event) => setRegistration({ ...registration, confirm: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              {error ? <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
              <button disabled={loading} className="btn-primary-cg w-full justify-center disabled:opacity-50"><UserPlus size={16} /> {loading ? "Creando..." : "Crear mi cuenta"}</button>
            </form>
          ) : mode === "forgot" ? (
            <form onSubmit={requestRecovery} className="space-y-4">
              <KeyRound size={24} className="text-[var(--brand-primary)]" /><h2 className="font-serif-cg text-2xl">Recupera tu contraseña</h2><p className="text-sm text-[var(--brand-muted)]">Te enviaremos un enlace para crear una contraseña nueva.</p>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Correo de la cuenta</span><input required type="email" autoComplete="email" value={recoveryEmail} onChange={(event) => setRecoveryEmail(event.target.value)} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              {error ? <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
              <button disabled={loading} className="btn-primary-cg w-full justify-center disabled:opacity-50"><Mail size={16} /> {loading ? "Enviando..." : "Enviar enlace"}</button>
              <button type="button" onClick={() => changeMode("login")} className="w-full text-sm text-[var(--brand-primary)] underline">Volver a iniciar sesión</button>
            </form>
          ) : mode === "reset" ? (
            <form onSubmit={resetPassword} className="space-y-4">
              <KeyRound size={24} className="text-[var(--brand-primary)]" /><h2 className="font-serif-cg text-2xl">Crea una contraseña nueva</h2>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Nueva contraseña</span><input required type="password" minLength={8} autoComplete="new-password" value={resetPasswords.password} onChange={(event) => setResetPasswords({ ...resetPasswords, password: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Confirmar contraseña</span><input required type="password" minLength={8} autoComplete="new-password" value={resetPasswords.confirm} onChange={(event) => setResetPasswords({ ...resetPasswords, confirm: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              {error ? <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
              <button disabled={loading} className="btn-primary-cg w-full justify-center disabled:opacity-50"><KeyRound size={16} /> {loading ? "Guardando..." : "Guardar contraseña"}</button>
            </form>
          ) : mode === "check-email" || mode === "recovery-sent" ? (
            <div className="py-5 text-center">
              <CheckCircle2 className="mx-auto text-[var(--brand-primary)]" size={36} /><h2 className="font-serif-cg text-2xl mt-4">Revisa tu correo</h2><p className="text-sm text-[var(--brand-muted)] mt-3">{message || "Enviamos las instrucciones. Revisa también la carpeta de correo no deseado."}</p>
              {error ? <div className="mt-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
              {mode === "check-email" ? <button type="button" disabled={loading} onClick={resendVerification} className="btn-secondary-cg mt-5 mx-auto disabled:opacity-50">Reenviar verificación</button> : null}
              <button type="button" onClick={() => changeMode("login")} className="block mx-auto mt-4 text-sm text-[var(--brand-primary)] underline">Volver a iniciar sesión</button>
            </div>
          ) : (
            <form onSubmit={login} className="space-y-4">
              <label className="block"><span className="overline text-[var(--brand-muted)]">Correo electrónico</span><div className="relative mt-2"><Mail size={16} className="absolute left-3 top-3.5 text-[var(--brand-muted)]" /><input required type="email" autoComplete="email" value={credentials.email} onChange={(event) => setCredentials({ ...credentials, email: event.target.value })} className="w-full border border-[var(--brand-border)] py-3 pl-10 pr-3 outline-none focus:border-[var(--brand-primary)]" /></div></label>
              <label className="block"><span className="overline text-[var(--brand-muted)]">Contraseña</span><input required type="password" autoComplete="current-password" value={credentials.password} onChange={(event) => setCredentials({ ...credentials, password: event.target.value })} className="mt-2 w-full border border-[var(--brand-border)] px-3 py-3 outline-none focus:border-[var(--brand-primary)]" /></label>
              {error ? <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
              <button disabled={loading} className="btn-primary-cg w-full justify-center disabled:opacity-50"><LogIn size={16} /> {loading ? "Entrando..." : "Iniciar sesión"}</button>
              <button type="button" onClick={() => changeMode("forgot")} className="w-full text-sm text-[var(--brand-primary)] underline">Olvidé mi contraseña</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
